import { Component, Prop, h, State, Element, Build, Host } from '@stencil/core';

const COMPONENT_NAME = 'sn-video-type-4';

/**
 * Video section component for SignNow integration and product pages, for self-hosted video
 * files instead of YouTube. Displays a centered heading and description above a full-width
 * native <video> player with a poster overlay and a click-to-play interaction. The video
 * container fades in from below when scrolled into the viewport (IntersectionObserver-based).
 *
 * Unlike `sn-video-type-1` (YouTube iframe), this type plays a direct video file URL
 * (mp4/webm) from any host — CDN, S3, Cloudinary, Pexels, etc. — via a native <video>
 * element with standard browser controls, so it works with sources other than YouTube.
 *
 * Card descriptions (heading, description) are slotted so translated text is injected
 * by Herald before SSR and renders correctly on localised pages.
 *
 * @product signnow
 * @category SignNow
 * @slot heading - {required}, <me-heading> - Section heading above the video player
 * @slot description - <me-text> - Section description below the heading
 * @example
 *   <sn-video-type-4
 *     video-src="https://videos.pexels.com/video-files/4904755/4904755-hd_1920_1080_24fps.mp4"
 *     video-title="See how easy it is to transform your lease renewal workflows"
 *     poster-alt="SignNow lease renewal workflow video preview"
 *     poster-src="https://marketing-static.signnow.com/386/images/integrations/video-integration.png"
 *     poster-srcset="https://marketing-static.signnow.com/386/images/integrations/video-integration.png 1x, https://marketing-static.signnow.com/386/images/integrations/video-integration@2x.png 2x"
 *     poster-webp-src="https://marketing-static.signnow.com/386/images/integrations/video-integration.webp"
 *     poster-webp-srcset="https://marketing-static.signnow.com/386/images/integrations/video-integration.webp 1x, https://marketing-static.signnow.com/386/images/integrations/video-integration@2x.webp 2x"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2" align="center">See how easy it is to transform your lease renewal workflows</me-heading>
 *     <me-text slot="description" align="center">Watch the integration in action and see how you can start effortlessly signing agreements.</me-text>
 *   </sn-video-type-4>
 */
@Component({
  tag: 'sn-video-type-4',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnVideoType4 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Direct video file URL (mp4/webm) from any host — CDN, S3, Cloudinary, Pexels, etc.
   * @mediaType video
   */
  @Prop({ attribute: 'video-src' }) videoSrc!: string;

  /**
   * Accessible label for the video player. Read by screen readers in place of visual content.
   * @content
   */
  @Prop({ attribute: 'video-title' }) videoTitle!: string;

  /**
   * Optional poster image shown before playback starts (PNG/JPG).
   * @mediaType image
   */
  @Prop({ attribute: 'poster-src' }) posterSrc?: string;

  /**
   * Optional srcset for the poster image. Provide both a 1x and a 2x candidate with density descriptors so standard screens don't download the heavier retina asset, e.g. `image.png 1x, image@2x.png 2x`.
   * @mediaType image
   */
  @Prop({ attribute: 'poster-srcset' }) posterSrcSet?: string;

  /**
   * Optional WebP version of the poster image for modern browsers. Must point to a real `.webp` file — a PNG/JPG here defeats the WebP optimisation and ships the larger original.
   * @mediaType image
   */
  @Prop({ attribute: 'poster-webp-src' }) posterWebpSrc?: string;

  /**
   * Optional srcset for the WebP poster image. Provide both a 1x and a 2x `.webp` candidate with density descriptors, e.g. `image.webp 1x, image@2x.webp 2x`.
   * @mediaType image
   */
  @Prop({ attribute: 'poster-webp-srcset' }) posterWebpSrcSet?: string;

  /**
   * Alt text for the poster image, describing what it shows — important for SEO and
   * screen readers. When empty, falls back to `videoTitle`.
   * @content
   */
  @Prop({ attribute: 'poster-alt' }) posterAlt?: string;

  @State() private playing: boolean = false;

  private observer?: IntersectionObserver;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    this.initFadeAnimation();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    this.observer?.disconnect();
  }

  private initFadeAnimation() {
    const wrapper = this.el.querySelector<HTMLElement>(`.${COMPONENT_NAME}__video-wrapper`);
    if (!wrapper) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            wrapper.classList.add('is-visible');
            this.observer?.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      this.observer.observe(wrapper);
    } else {
      wrapper.classList.add('is-visible');
    }
  }

  private handlePlay = () => {
    this.playing = true;
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handlePlay();
    }
  };

  private renderPreview() {
    const hasWebp = !!(this.posterWebpSrc || this.posterWebpSrcSet);
    const hasPoster = !!(this.posterSrc || this.posterSrcSet || hasWebp);

    return (
      <div class={`${COMPONENT_NAME}__player ${COMPONENT_NAME}__player--preview`} aria-live="polite">
        {hasPoster && (
          <me-picture
            class={`${COMPONENT_NAME}__thumbnail`}
            src={this.posterSrc}
            srcSet={this.posterSrcSet}
            webpSrcSet={hasWebp ? this.posterWebpSrcSet || this.posterWebpSrc : undefined}
            aspectRatio="16/9"
            alt={this.posterAlt || this.videoTitle}
          />
        )}
        <button class={`${COMPONENT_NAME}__play-btn`} type="button" aria-label="Play video" tabIndex={0} onClick={this.handlePlay} onKeyDown={this.handleKeyDown}>
          <svg class={`${COMPONENT_NAME}__play-icon`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 94 94" width="94" height="94" aria-hidden="true" focusable="false">
            <g fill="none">
              <circle class={`${COMPONENT_NAME}__play-circle`} cx="47" cy="47" r="47" />
              <path
                class={`${COMPONENT_NAME}__play-triangle`}
                d="M62.078 48.21 39.192 62.62A1.43 1.43 0 0 1 37 61.41V32.59a1.43 1.43 0 0 1 2.192-1.21l22.886 14.41a1.43 1.43 0 0 1 0 2.42"
              />
            </g>
          </svg>
        </button>
      </div>
    );
  }

  private renderPlayer() {
    return (
      <div class={`${COMPONENT_NAME}__player`} aria-live="polite">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video class={`${COMPONENT_NAME}__video`} src={this.videoSrc} poster={this.posterSrc} aria-label={this.videoTitle} controls autoplay playsinline />
      </div>
    );
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="4xl">
          <div class={`${COMPONENT_NAME}__intro`}>
            <slot name="heading" />
            <slot name="description" />
          </div>
          <div class={`${COMPONENT_NAME}__video-wrapper`}>{this.playing ? this.renderPlayer() : this.renderPreview()}</div>
        </me-section>
      </Host>
    );
  }
}
